import React from "react";

/**
 * Handball half-court zone picker.
 * Renders a top-down view of the attacking half with 6m and 9m lines.
 * Zones (V1-V3, H1-H3) are wedge-shaped sectors radiating from the goal midpoint.
 * Click a sector to select that zone.
 *
 * Props:
 *   value: current zone id or null
 *   onChange: (zoneId) => void
 */
const CENTER = { x: 200, y: 0 };
const OUTER = 275; // sector radius (extends beyond court, clipped)
const R6M = 62;
const R9M = 96;
const COURT = { x: 15, y: 0, w: 370, h: 200 };

const ZONES = [
  { id: "V1", label: "V1", sub: "Yttre",       a1: 150, a2: 180 },
  { id: "V2", label: "V2", sub: "9m",          a1: 120, a2: 150 },
  { id: "V3", label: "V3", sub: "Mitt",        a1: 90,  a2: 120 },
  { id: "H3", label: "H3", sub: "Mitt",        a1: 60,  a2: 90  },
  { id: "H2", label: "H2", sub: "9m",          a1: 30,  a2: 60  },
  { id: "H1", label: "H1", sub: "Yttre",       a1: 0,   a2: 30  },
];

const rad = (d) => (d * Math.PI) / 180;
const pt = (deg, r) => ({
  x: CENTER.x + r * Math.cos(rad(deg)),
  y: CENTER.y + r * Math.sin(rad(deg)),
});

const sectorPath = (a1, a2, r) => {
  const p1 = pt(a1, r);
  const p2 = pt(a2, r);
  return `M ${CENTER.x} ${CENTER.y} L ${p1.x} ${p1.y} A ${r} ${r} 0 0 1 ${p2.x} ${p2.y} Z`;
};

const arcPath = (r) => {
  const p1 = pt(180, r);
  const p2 = pt(0, r);
  return `M ${p1.x} ${p1.y} A ${r} ${r} 0 0 0 ${p2.x} ${p2.y}`;
};

export default function HandballCourt({ value, onChange, disabled = false }) {
  return (
    <div className="w-full" data-testid="handball-court">
      <svg
        viewBox="0 -18 400 228"
        className="w-full h-auto block select-none"
        role="group"
        aria-label="Välj skottets zon på planen"
      >
        <defs>
          <clipPath id="court-clip">
            <rect x={COURT.x} y={COURT.y} width={COURT.w} height={COURT.h} />
          </clipPath>
        </defs>

        {/* Court background */}
        <rect
          x={COURT.x}
          y={COURT.y}
          width={COURT.w}
          height={COURT.h}
          fill="#000000"
        />

        {/* Sectors (clipped to court) */}
        <g clipPath="url(#court-clip)">
          {ZONES.map((z) => {
            const selected = value === z.id;
            return (
              <path
                key={z.id}
                d={sectorPath(z.a1, z.a2, OUTER)}
                fill={
                  selected
                    ? "rgba(52, 199, 89, 0.55)"
                    : "rgba(255, 255, 255, 0.02)"
                }
                stroke="#ffffff"
                strokeWidth="1.4"
                style={{
                  cursor: disabled ? "not-allowed" : "pointer",
                  transition: "fill 160ms ease",
                }}
                onClick={() => !disabled && onChange(z.id)}
                onMouseEnter={(e) => {
                  if (disabled || selected) return;
                  e.currentTarget.setAttribute(
                    "fill",
                    "rgba(52, 199, 89, 0.18)"
                  );
                }}
                onMouseLeave={(e) => {
                  if (disabled || selected) return;
                  e.currentTarget.setAttribute(
                    "fill",
                    "rgba(255, 255, 255, 0.02)"
                  );
                }}
                data-testid={`court-zone-${z.id}`}
                data-active={selected}
              />
            );
          })}
        </g>

        {/* 6m arc */}
        <path
          d={arcPath(R6M)}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
          clipPath="url(#court-clip)"
        />
        {/* 9m arc (dashed) */}
        <path
          d={arcPath(R9M)}
          fill="none"
          stroke="#ffffff"
          strokeWidth="1.8"
          strokeDasharray="6 4"
          clipPath="url(#court-clip)"
        />

        {/* Goal line (top) */}
        <line
          x1={COURT.x}
          y1={0}
          x2={COURT.x + COURT.w}
          y2={0}
          stroke="#ffffff"
          strokeWidth="2"
        />
        {/* Goal (3m) */}
        <line x1={185} y1={0} x2={215} y2={0} stroke="#34C759" strokeWidth="6" />

        {/* Court outer rectangle border (on top) */}
        <rect
          x={COURT.x}
          y={COURT.y}
          width={COURT.w}
          height={COURT.h}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
        />

        {/* Zone labels */}
        {ZONES.map((z) => {
          const mid = (z.a1 + z.a2) / 2;
          const labelR = mid >= 60 && mid <= 120 ? 175 : 150;
          const p = pt(mid, labelR);
          return (
            <g key={`lbl-${z.id}`} pointerEvents="none">
              <text
                x={p.x}
                y={p.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#ffffff"
                fontFamily="Barlow Condensed, Arial, sans-serif"
                fontWeight="900"
                fontSize="22"
                letterSpacing="3"
              >
                {z.label}
              </text>
              <text
                x={p.x}
                y={p.y + 15}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#A1A1AA"
                fontFamily="Barlow Condensed, Arial, sans-serif"
                fontSize="9"
                letterSpacing="2"
              >
                {z.sub.toUpperCase()}
              </text>
            </g>
          );
        })}

        {/* Small "MÅL" marker above goal */}
        <text
          x={200}
          y={-6}
          textAnchor="middle"
          fill="#34C759"
          fontSize="10"
          fontFamily="Barlow Condensed, Arial, sans-serif"
          letterSpacing="3"
          fontWeight="900"
          pointerEvents="none"
        >
          MÅL
        </text>
      </svg>
    </div>
  );
}
